import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, ChevronUp, Info, Plus, LockKeyhole } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Checkbox } from "@/components/ui/checkbox";

const bountyTypes = [
  "Consumer-Driven",
  "Enterprise",
  "Community",
  "Research",
  "Innovation"
];

const ispaiMembers = [
  "Chris Clayton",
  "Corey Vickers",
  "Giblet",
  "Jess Simm",
  "Jim Lawnin",
  "Karen Watson",
  "Miguel Escobedo",
  "Mike Mosher",
  "Morgan Eldred",
  "Pete Dumont",
  "Shaan Ray",
  "Trey Fleming",
  "Yannis Kalfoglou"
];

// Escrow condition examples
const escrowConditionExamples = [
  "Funds will be held in escrow until the solution is approved by the Creator and at least two members of the review committee.",
  "50% of the bounty will be held in escrow until the solution passes initial acceptance testing, and the remaining 50% will be held until the solution passes all performance requirements.",
  "Funds will remain in escrow for 30 days after initial solution delivery to allow for thorough testing and validation.",
  "Escrow will be maintained until the solution demonstrates successful integration with existing systems as verified by the technical team.",
  "Bounty will be held in escrow until the solution meets all specified requirements and receives confirmation from both the Creator and Confirmer roles."
];

// Release condition examples
const releaseConditionExamples = [
  "Funds will be released upon successful demonstration of the solution meeting all specified requirements and approval by the Creator.",
  "Release of funds will occur in three stages: 30% upon acceptance of the initial prototype, 30% after successful integration testing, and 40% upon final acceptance.",
  "Funds will be released after a 30-day testing period with no critical issues reported by the user community.",
  "The bounty will be released once the solution has been verified to meet or exceed all performance metrics outlined in the requirements document.",
  "Release of the bounty requires unanimous approval from the Creator, Confirmer, and at least one subject matter expert from the requesting organization."
];

const DemoCallForSolutionsBounty = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isBounty, setIsBounty] = useState<string | null>(null);
  const [selectedArea, setSelectedArea] = useState<string>(""); 
  const [isAreaExpanded, setIsAreaExpanded] = useState(false);
  const [selectedCurator, setSelectedCurator] = useState<string>("");
  const [includePhoneNumber, setIncludePhoneNumber] = useState(false);
  const [confirmerName, setConfirmerName] = useState<string>("");
  const [contributors, setContributors] = useState<string[]>([]);
  
  const functionalAreas = [
    { id: "technical-architecture", name: "Technical Architecture" },
    { id: "web3-integration", name: "Web3 Integration" },
    { id: "user-experience", name: "User Experience (UX/UI)" },
    { id: "compliance-governance", name: "Compliance & Governance" },
    { id: "ai-automation", name: "AI & Automation" },
    { id: "knowledge-management", name: "Knowledge Management" },
    { id: "branding", name: "Branding" },
    { id: "value-proposition", name: "Value Proposition" }
  ];
  
  const [bountyAmount, setBountyAmount] = useState("");
  const [bountyType, setBountyType] = useState("");
  
  // Escrow and release conditions state
  const [escrowConditions, setEscrowConditions] = useState("");
  const [selectedEscrowCondition, setSelectedEscrowCondition] = useState<string | null>(null);
  const [customEscrowCondition, setCustomEscrowCondition] = useState("");
  
  const [releaseConditions, setReleaseConditions] = useState("");
  const [selectedReleaseCondition, setSelectedReleaseCondition] = useState<string | null>(null);
  const [customReleaseCondition, setCustomReleaseCondition] = useState("");
  
  const [curatorPercentage, setCuratorPercentage] = useState("40");
  const [contributorPercentage, setContributorPercentage] = useState("30");
  const [confirmerPercentage, setConfirmerPercentage] = useState("10");
  const [composerPercentage, setComposerPercentage] = useState("20");

  const [curatorAssignment, setCuratorAssignment] = useState<"open" | "assign">("open");
  const [contributorAssignment, setContributorAssignment] = useState<"open" | "assign">("open");
  const [confirmerAssignment, setConfirmerAssignment] = useState<"open" | "assign">("open");
  const [composerAssignment, setComposerAssignment] = useState<"open" | "assign">("open");
  
  const [curatorTeamMember, setCuratorTeamMember] = useState("");
  const [contributorTeamMember, setContributorTeamMember] = useState("");
  const [confirmerTeamMember, setConfirmerTeamMember] = useState("");
  const [composerTeamMember, setComposerTeamMember] = useState("");

  useEffect(() => {
    if (location.state && location.state.selectedArea) {
      const area = location.state.selectedArea;
      setSelectedArea(area);
      
      const areaName = functionalAreas.find(a => a.id === area)?.name || area;
      setSelectedArea(areaName);
    }
  }, [location]);

  const handleBack = () => {
    navigate("/demo-call-for-solutions/functional-area");
  };

  const handleAddContributor = () => {
    setContributors([...contributors, ""]);
  };

  const handleContributorChange = (index: number, value: string) => {
    const updatedContributors = [...contributors];
    updatedContributors[index] = value;
    setContributors(updatedContributors);
  };

  // Handle escrow condition selection
  useEffect(() => {
    if (selectedEscrowCondition === "other") {
      setEscrowConditions(customEscrowCondition);
    } else if (selectedEscrowCondition !== null) {
      setEscrowConditions(selectedEscrowCondition);
    }
  }, [selectedEscrowCondition, customEscrowCondition]);

  // Handle release condition selection
  useEffect(() => {
    if (selectedReleaseCondition === "other") {
      setReleaseConditions(customReleaseCondition);
    } else if (selectedReleaseCondition !== null) {
      setReleaseConditions(selectedReleaseCondition);
    }
  }, [selectedReleaseCondition, customReleaseCondition]);

  useEffect(() => {
    if (isBounty === "yes") {
      setBountyAmount("10000");
      setBountyType("community");
      
      // Set default escrow and release conditions
      const defaultEscrowCondition = escrowConditionExamples[0];
      setSelectedEscrowCondition(defaultEscrowCondition);
      setEscrowConditions(defaultEscrowCondition);
      
      const defaultReleaseCondition = releaseConditionExamples[0];
      setSelectedReleaseCondition(defaultReleaseCondition);
      setReleaseConditions(defaultReleaseCondition);
      
      setCuratorAssignment("assign");
      setCuratorTeamMember("Karen Watson");
      setCuratorPercentage("50");
      
      setContributorAssignment("assign");
      setContributorTeamMember("Corey Vickers");
      setContributorPercentage("45");
      
      setConfirmerAssignment("assign");
      setConfirmerTeamMember("Giblet");
      
      setComposerAssignment("assign");
      setComposerTeamMember("Pete Dumont");
      setComposerPercentage("33");
    }
  }, [isBounty]);

  const handleContinue = () => {
    if (isBounty === "yes") {
      navigate("/demo-call-for-solutions/bounty-details", { 
        state: { 
          selectedArea, 
          isBounty,
          bountyDetails: {
            bountyAmount,
            bountyType,
            escrowConditions,
            releaseConditions,
            attribution: {
              curatorPercentage,
              contributorPercentage,
              confirmerPercentage,
              composerPercentage
            },
            roleAssignments: {
              curator: {
                status: curatorAssignment,
                assignedTo: curatorAssignment === "assign" ? curatorTeamMember : "Open"
              },
              contributor: {
                status: contributorAssignment,
                assignedTo: contributorAssignment === "assign" ? contributorTeamMember : "Open"
              },
              confirmer: {
                status: confirmerAssignment,
                assignedTo: confirmerAssignment === "assign" ? confirmerTeamMember : "Open"
              },
              composer: {
                status: composerAssignment,
                assignedTo: composerAssignment === "assign" ? composerTeamMember : "Open"
              }
            }
          }
        } 
      });
    } else {
      navigate("/demo-call-for-solutions/curator", { 
        state: { 
          selectedArea, 
          isBounty,
          selectedCurator,
          teamAssignment: {
            confirmer: confirmerName,
            contributors
          }
        } 
      });
    }
  };

  useEffect(() => {
    if (confirmerTeamMember === "Giblet") {
      setConfirmerPercentage("5");
    } else if (confirmerPercentage === "5" && confirmerTeamMember !== "Giblet") {
      setConfirmerPercentage("10");
    }
  }, [confirmerTeamMember, confirmerPercentage]);

  return (
    <div className="min-h-screen bg-white flex flex-col items-center p-4 pb-12">
      <div className="mt-12 mb-6">
        <img 
          src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
          alt="iSPAI Logo" 
          className="h-16 cursor-pointer"
          onClick={() => navigate("/engage")}
        />
      </div>

      <div className="w-full max-w-2xl space-y-6">
        <Card className="border border-gray-200 overflow-hidden">
          <div 
            className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
            onClick={() => setIsAreaExpanded(!isAreaExpanded)}
          >
            <div>
              <h2 className="text-xl font-semibold">Select Functional Area</h2>
              <p className="text-gray-600 mt-1">Selected: {selectedArea}</p>
            </div>
            <div>
              {isAreaExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
          </div>
        </Card>

        <Card className="border border-gray-200">
          <CardContent className="p-6">
            <h2 className="text-xl font-semibold mb-4">Solution Bounty</h2>
            <p className="text-gray-600 mb-4">Is this a solution bounty?</p>
            
            <RadioGroup 
              value={isBounty || ""} 
              onValueChange={(value) => {
                setIsBounty(value);
                if (value === "yes") {
                  setSelectedCurator("");
                }
              }}
              className="flex space-x-8"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="yes" id="yes" />
                <Label htmlFor="yes">Yes</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="no" id="no" />
                <Label htmlFor="no">No</Label>
              </div>
            </RadioGroup>
          </CardContent>
        </Card>

        {isBounty === "no" && (
          <Card className="border border-gray-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-2">Assign Creator</h2>
              <p className="text-gray-600 mb-4">Select and provide creator's contact information</p>
              
              <Select onValueChange={setSelectedCurator} value={selectedCurator}>
                <SelectTrigger className="w-full mb-4">
                  <SelectValue placeholder="Select creator" />
                </SelectTrigger>
                <SelectContent>
                  {ispaiMembers.map(member => (
                    <SelectItem key={member} value={member}>
                      {member}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {selectedCurator && (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Email Address
                    </label>
                    <Input type="email" placeholder="Enter creator's email" />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Slack Handle
                    </label>
                    <Input type="text" placeholder="Enter creator's Slack handle" />
                  </div>
                  
                  <div className="flex items-center space-x-2 mt-2">
                    <Checkbox 
                      id="include-phone" 
                      checked={includePhoneNumber}
                      onCheckedChange={(checked) => setIncludePhoneNumber(checked as boolean)}
                    />
                    <label 
                      htmlFor="include-phone" 
                      className="text-sm text-gray-700 cursor-pointer"
                    >
                      Include phone number for calls or texts
                    </label>
                  </div>
                  
                  {includePhoneNumber && (
                    <div className="mt-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number
                      </label>
                      <Input type="tel" placeholder="Enter creator's phone number" />
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {isBounty === "no" && selectedCurator && (
          <Card className="border border-gray-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-2">Team Assignment</h2>
              <p className="text-gray-600 mb-6">Add contributors and confirmer details</p>
              
              <div className="mb-8">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="font-medium">Contributors</h3>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={handleAddContributor}
                    className="flex items-center gap-1"
                  >
                    <Plus size={16} />
                    Add Contributor
                  </Button>
                </div>
                
                {contributors.length > 0 ? (
                  <div className="space-y-4">
                    {contributors.map((contributor, index) => (
                      <div key={index} className="p-4 border rounded-md">
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Name
                            </label>
                            <Select 
                              value={contributor} 
                              onValueChange={(value) => handleContributorChange(index, value)}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select contributor" />
                              </SelectTrigger>
                              <SelectContent>
                                {ispaiMembers
                                  .filter(member => member !== selectedCurator && member !== confirmerName)
                                  .map(member => (
                                    <SelectItem key={member} value={member}>
                                      {member}
                                    </SelectItem>
                                  ))
                                }
                              </SelectContent>
                            </Select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Email
                            </label>
                            <Input type="email" placeholder="Enter contributor's email" />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Slack Handle
                            </label>
                            <Input type="text" placeholder="Enter contributor's Slack handle" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-6 border border-dashed rounded-md">
                    <p className="text-gray-500">No contributors added yet</p>
                  </div>
                )}
              </div>
              
              <div>
                <h3 className="font-medium mb-3">Confirmer Details</h3>
                <div className="p-4 border rounded-md">
                  <p className="text-sm text-gray-600 mb-4">Responsible for reviewing and validating solutions</p>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <Select value={confirmerName} onValueChange={setConfirmerName}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select confirmer" />
                        </SelectTrigger>
                        <SelectContent>
                          {ispaiMembers
                            .filter(member => member !== selectedCurator)
                            .map(member => (
                              <SelectItem key={member} value={member}>
                                {member}
                              </SelectItem>
                            ))
                          }
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {confirmerName && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Email
                          </label>
                          <Input type="email" placeholder="Enter confirmer's email" />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Slack Handle
                          </label>
                          <Input type="text" placeholder="Enter confirmer's Slack handle" />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {isBounty === "yes" && (
          <Card className="border border-gray-200">
            <CardContent className="p-6">
              <h2 className="text-xl font-semibold mb-2">Solution Bounty Details</h2>
              <p className="text-gray-600 mb-4">Define the bounty amount, type, and attribution structure</p>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bounty Amount</label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={bountyAmount}
                    onChange={(e) => setBountyAmount(e.target.value)}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bounty Type</label>
                  <Select onValueChange={setBountyType} value={bountyType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Consumer-Driven" />
                    </SelectTrigger>
                    <SelectContent>
                      {bountyTypes.map(type => (
                        <SelectItem key={type} value={type.toLowerCase()}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-sm font-medium text-gray-700">Escrow Conditions</label>
                    <TooltipProvider>
                      <Popover>
                        <PopoverTrigger asChild>
                          <div className="cursor-help">
                            <Info size={14} className="text-gray-500" />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 max-w-sm text-sm space-y-2">
                          <p className="font-semibold">Select one of the escrow conditions below or create your own:</p>
                          <RadioGroup 
                            value={selectedEscrowCondition || ""} 
                            onValueChange={setSelectedEscrowCondition}
                            className="space-y-2"
                          >
                            {escrowConditionExamples.map((condition, index) => (
                              <div key={index} className="flex items-start gap-2">
                                <RadioGroupItem id={`escrow-${index}`} value={condition} className="mt-1" />
                                <Label htmlFor={`escrow-${index}`} className="text-sm font-normal">{condition}</Label>
                              </div>
                            ))}
                            
                            <div className="flex items-start gap-2 pt-2 border-t">
                              <RadioGroupItem id="escrow-other" value="other" className="mt-1" />
                              <div className="space-y-1 flex-1">
                                <Label htmlFor="escrow-other" className="text-sm font-normal">Other (specify below)</Label>
                                <Textarea 
                                  placeholder="Custom escrow conditions..." 
                                  value={customEscrowCondition}
                                  onChange={(e) => setCustomEscrowCondition(e.target.value)}
                                  disabled={selectedEscrowCondition !== "other"}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          </RadioGroup>
                        </PopoverContent>
                      </Popover>
                    </TooltipProvider>
                  </div>
                  <Textarea 
                    placeholder="Specify the conditions for escrow..." 
                    value={escrowConditions}
                    onChange={(e) => {
                      setEscrowConditions(e.target.value);
                      // If manually edited, select "Other" and update custom text
                      if (selectedEscrowCondition !== "other") {
                        setSelectedEscrowCondition("other");
                        setCustomEscrowCondition(e.target.value);
                      } else {
                        setCustomEscrowCondition(e.target.value);
                      }
                    }}
                  />
                </div>
                
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <label className="block text-sm font-medium text-gray-700">Release Conditions</label>
                    <TooltipProvider>
                      <Popover>
                        <PopoverTrigger asChild>
                          <div className="cursor-help">
                            <Info size={14} className="text-gray-500" />
                          </div>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 max-w-sm text-sm space-y-2">
                          <p className="font-semibold">Select one of the release conditions below or create your own:</p>
                          <RadioGroup 
                            value={selectedReleaseCondition || ""} 
                            onValueChange={setSelectedReleaseCondition}
                            className="space-y-2"
                          >
                            {releaseConditionExamples.map((condition, index) => (
                              <div key={index} className="flex items-start gap-2">
                                <RadioGroupItem id={`release-${index}`} value={condition} className="mt-1" />
                                <Label htmlFor={`release-${index}`} className="text-sm font-normal">{condition}</Label>
                              </div>
                            ))}
                            
                            <div className="flex items-start gap-2 pt-2 border-t">
                              <RadioGroupItem id="release-other" value="other" className="mt-1" />
                              <div className="space-y-1 flex-1">
                                <Label htmlFor="release-other" className="text-sm font-normal">Other (specify below)</Label>
                                <Textarea 
                                  placeholder="Custom release conditions..." 
                                  value={customReleaseCondition}
                                  onChange={(e) => setCustomReleaseCondition(e.target.value)}
                                  disabled={selectedReleaseCondition !== "other"}
                                  className="mt-1"
                                />
                              </div>
                            </div>
                          </RadioGroup>
                        </PopoverContent>
                      </Popover>
                    </TooltipProvider>
                  </div>
                  <Textarea 
                    placeholder="Specify the conditions for release..." 
                    value={releaseConditions}
                    onChange={(e) => {
                      setReleaseConditions(e.target.value);
                      // If manually edited, select "Other" and update custom text
                      if (selectedReleaseCondition !== "other") {
                        setSelectedReleaseCondition("other");
                        setCustomReleaseCondition(e.target.value);
                      } else {
                        setCustomReleaseCondition(e.target.value);
                      }
                    }}
                  />
                </div>
                
                <div>
                  <h3 className="text-md font-medium mb-3">Attribution Structure & Role Assignment</h3>
                  
                  <div className="space-y-4">
                    <div className="border rounded-md p-3">
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                        <div className="flex items-center w-full md:w-1/3">
                          <span className="w-24 text-sm">Curator</span>
                          <Input
                            type="number"
                            className="w-16 mr-2"
                            value={curatorPercentage}
                            onChange={(e) => {
                              if (curatorAssignment === "assign" && curatorTeamMember !== "Giblet") {
                                setCuratorPercentage(e.target.value);
                              }
                            }}
                            readOnly={curatorAssignment === "assign" && curatorTeamMember === "Giblet"}
                          />
                          <span className="text-sm">%</span>
                        </div>
                        
                        <div className="flex-grow">
                          <Select 
                            value={curatorAssignment} 
                            onValueChange={(value: "open" | "assign") => setCuratorAssignment(value)}
                          >
                            <SelectTrigger className="w-full md:w-40">
                              <SelectValue placeholder="Open" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open Call</SelectItem>
                              <SelectItem value="assign">Assign</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {curatorAssignment === "assign" && (
                          <div className="flex-grow">
                            <Select value={curatorTeamMember} onValueChange={setCuratorTeamMember}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select team member" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {ispaiMembers.map(member => (
                                  <SelectItem key={member} value={member}>
                                    {member}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="border rounded-md p-3">
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                        <div className="flex items-center w-full md:w-1/3">
                          <span className="w-24 text-sm">Contributor</span>
                          <Input
                            type="number"
                            className="w-16 mr-2"
                            value={contributorPercentage}
                            onChange={(e) => {
                              if (contributorAssignment === "assign" && contributorTeamMember !== "Giblet") {
                                setContributorPercentage(e.target.value);
                              }
                            }}
                            readOnly={contributorAssignment === "assign" && contributorTeamMember === "Giblet"}
                          />
                          <span className="text-sm">%</span>
                        </div>
                        
                        <div className="flex-grow">
                          <Select 
                            value={contributorAssignment} 
                            onValueChange={(value: "open" | "assign") => setContributorAssignment(value)}
                          >
                            <SelectTrigger className="w-full md:w-40">
                              <SelectValue placeholder="Open" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open Call</SelectItem>
                              <SelectItem value="assign">Assign</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {contributorAssignment === "assign" && (
                          <div className="flex-grow">
                            <Select value={contributorTeamMember} onValueChange={setContributorTeamMember}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select team member" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {ispaiMembers.map(member => (
                                  <SelectItem key={member} value={member}>
                                    {member}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="border rounded-md p-3">
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                        <div className="flex items-center w-full md:w-1/3">
                          <span className="w-24 text-sm">Confirmer</span>
                          <Input
                            type="number"
                            className="w-16 mr-2"
                            value={confirmerPercentage}
                            onChange={(e) => {
                              if (confirmerTeamMember !== "Giblet") {
                                setConfirmerPercentage(e.target.value);
                              }
                            }}
                            readOnly={confirmerTeamMember === "Giblet"}
                          />
                          <span className="text-sm">%</span>
                        </div>
                        
                        <div className="flex-grow">
                          <Select 
                            value={confirmerAssignment} 
                            onValueChange={(value: "open" | "assign") => setConfirmerAssignment(value)}
                          >
                            <SelectTrigger className="w-full md:w-40">
                              <SelectValue placeholder="Open" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open Call</SelectItem>
                              <SelectItem value="assign">Assign</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {confirmerAssignment === "assign" && (
                          <div className="flex-grow">
                            <Select 
                              value={confirmerTeamMember} 
                              onValueChange={setConfirmerTeamMember}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select team member" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {ispaiMembers.map(member => (
                                  <SelectItem key={member} value={member}>
                                    {member}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                      
                      {confirmerTeamMember === "Giblet" && (
                        <div className="mt-3 p-2 bg-gray-50 rounded-md flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <LockKeyhole size={16} className="text-blue-600" />
                            <span className="text-sm font-medium text-blue-600">Powered by Giblet</span>
                          </div>
                          <div>
                            <span className="text-xs text-gray-500">Confirmer Attribution: 5%</span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="border rounded-md p-3">
                      <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-4">
                        <div className="flex items-center w-full md:w-1/3">
                          <span className="w-24 text-sm">Composer</span>
                          <Input
                            type="number"
                            className="w-16 mr-2"
                            value={composerPercentage}
                            onChange={(e) => setComposerPercentage(e.target.value)}
                          />
                          <span className="text-sm">%</span>
                        </div>
                        
                        <div className="flex-grow">
                          <Select 
                            value={composerAssignment} 
                            onValueChange={(value: "open" | "assign") => setComposerAssignment(value)}
                          >
                            <SelectTrigger className="w-full md:w-40">
                              <SelectValue placeholder="Open" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="open">Open Call</SelectItem>
                              <SelectItem value="assign">Assign</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        
                        {composerAssignment === "assign" && (
                          <div className="flex-grow">
                            <Select value={composerTeamMember} onValueChange={setComposerTeamMember}>
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select team member" />
                              </SelectTrigger>
                              <SelectContent className="max-h-60">
                                {ispaiMembers.map(member => (
                                  <SelectItem key={member} value={member}>
                                    {member}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="flex justify-between">
          <Button 
            variant="outline" 
            onClick={handleBack}
            className="border-gray-300"
          >
            Back
          </Button>
          
          <Button 
            onClick={handleContinue}
            disabled={!isBounty || (isBounty === "no" && !selectedCurator)}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2 rounded"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DemoCallForSolutionsBounty;
